<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { Bell, ShieldCheck, Info, AlertTriangle, AlertCircle, UserPlus } from 'lucide-vue-next'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { Button, buttonVariants } from '@/components/ui/button'
import type { Notification, NotificationType } from '@/types/notifications'

type ButtonSize = NonNullable<Parameters<typeof buttonVariants>[0]>['size']

const props = withDefaults(defineProps<{
  popoverSide?: 'top' | 'right' | 'bottom' | 'left'
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left'
  buttonSize?: ButtonSize
}>(), {
  popoverSide: 'bottom',
  tooltipSide: 'right',
  buttonSize: 'nav',
})

const store = useStore()
const isOpen = ref(false)
const tooltipOpen = ref(false)

const notifications = computed<Notification[]>(
  () => store.getters['notifications/getNotifications']
)
const hasUnread = computed(() => store.getters['notifications/hasUnread'])

watch(isOpen, (open) => {
  if (!open) return
  tooltipOpen.value = false
  store.dispatch('project/fetchAllInvites', { silent: true })
})

const handleNotificationClick = (notification: Notification) => {
  // Mark as read
  store.dispatch('notifications/markAsRead', notification.id)

  // Execute action callback if provided
  if (notification.action?.handler) {
    notification.action.handler()
  }

  // Close popover
  isOpen.value = false
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'kyc_pending':
      return ShieldCheck
    case 'project_invite':
      return UserPlus
    case 'warning':
      return AlertTriangle
    case 'error':
      return AlertCircle
    case 'info':
    default:
      return Info
  }
}

const getIconColor = (type: NotificationType) => {
  switch (type) {
    case 'kyc_pending':
    case 'project_invite':
      return 'text-blue-600'
    case 'warning':
      return 'text-amber-500'
    case 'error':
      return 'text-red-500'
    case 'info':
    default:
      return 'text-muted-foreground'
  }
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
}
</script>

<template>
  <Tooltip :open="tooltipOpen" @update:open="(v: boolean) => tooltipOpen = v && !isOpen">
    <Popover v-model:open="isOpen">
      <TooltipTrigger as-child>
        <PopoverTrigger as-child>
          <Button variant="ghost" :size="buttonSize">
            <span class="relative inline-flex">
              <Bell class="h-5 w-5 text-muted-foreground" />
              <span
                v-if="hasUnread"
                class="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-background"
              />
            </span>
          </Button>
        </PopoverTrigger>
      </TooltipTrigger>
      <TooltipContent :side="tooltipSide" :side-offset="8">
        Notifications
      </TooltipContent>
    <PopoverContent class="w-80 p-0" :side="popoverSide" align="end" :side-offset="8">
      <div class="p-3 border-b">
        <h4 class="font-medium text-sm">Notifications</h4>
      </div>

      <div v-if="notifications.length === 0" class="p-6 text-center text-muted-foreground text-sm">
        No notifications
      </div>

      <div v-else class="max-h-80 overflow-y-auto">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="flex items-start gap-3 p-3 border-b last:border-b-0"
        >
          <div class="flex-shrink-0 mt-0.5">
            <component
              :is="getNotificationIcon(notification.type)"
              class="h-5 w-5"
              :class="getIconColor(notification.type)"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <h4 class="text-sm font-medium text-foreground">
                  {{ notification.title }}
                </h4>
                <p class="text-sm text-muted-foreground mt-0.5">
                  {{ notification.description }}
                </p>
              </div>
              <Button
                v-if="notification.action"
                size="sm"
                variant="outline"
                @click="handleNotificationClick(notification)"
                class="h-7 text-xs font-normal flex-shrink-0"
              >
                {{ notification.action.label || 'Open' }}
              </Button>
            </div>
          </div>
        </div>
      </div>
      </PopoverContent>
    </Popover>
  </Tooltip>
</template>
